'use client'
import { useEffect, useRef, useState } from 'react'
import s from './List.module.scss'
import { Typology } from '@/types/singles/category'
import { Image } from '@/types/common/ImageData'
import Link from 'next/link'
import LazyImage from '@/components/common/LazyImage'
import { set } from 'sanity'
import { motion } from 'framer-motion'

interface ProjectsListProps {
    projects: Array<DataProps>;
}

interface ImageProp {
    image: Image;
}

interface Status {
    title: string;
}

interface Region {
    title: string;
}
interface DataProps {
    title: string;
    slug: string;
    featuredImage: ImageProp;
    featuredImageHover: ImageProp | null;
    year: string;
    location: string | null;
    typologies: Array<Typology>;
    status: string | null;
    region: string | null;
}

export default function ProjectsListComponent({ projects }: ProjectsListProps) {

    const [sortedProjects, setSortedProjects] = useState([...projects]);
    const [sortConfig, setSortConfig] = useState<{ key: keyof DataProps | null; direction: 'asc' | 'desc' }>({ key: null, direction: 'asc' });
    const [hoveredProject, setHoveredProject] = useState<DataProps | null>(null);
    const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
    const imageRef = useRef<HTMLDivElement | null>(null);
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [showImage, setShowImage] = useState(false);

    useEffect(() => {
        setTimeout(() => {
            if (imageRef.current) {
                const { width, height } = imageRef.current.getBoundingClientRect();
                setImageSize({ width, height });
                setShowImage(true);
            }
        }, 20, imageRef.current);
            
        
    }, [hoveredProject]); 


    // 🟢 Cuando los proyectos filtrados cambian, aplicar orden actual si ya existe
    useEffect(() => {
        if (sortConfig.key) {
            sortProjects(sortConfig.key, sortConfig.direction);
        } else {
            setSortedProjects([...projects]);
        }
    }, [projects]);

    const sortProjects = (key: keyof DataProps, direction: 'asc' | 'desc' = 'asc') => {
        const sorted = [...projects].sort((a, b) => {
            if (!a[key] || !b[key]) return 0;
            if (key === 'year') {
                return direction === 'asc' ? Number(a[key]) - Number(b[key]) : Number(b[key]) - Number(a[key]);
            }
            const valueA = typeof a[key] === 'string' ? a[key].toString().toLowerCase() : '';
            const valueB = typeof b[key] === 'string' ? b[key].toString().toLowerCase() : '';
            return direction === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
        });

        setSortedProjects(sorted);
        setSortConfig({ key, direction });
    };

    

    return (
        <div className={s.projectsList}>
            <div className={s.header}>
                <button
                    onClick={() => sortProjects('title', sortConfig.direction === 'asc' ? 'desc' : 'asc')}
                >
                    PROJECT NAME 
                    <span className={`${s.arrow} ${sortConfig.key === 'title' && sortConfig.direction === 'asc' ? s.active : ''}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="9" viewBox="0 0 16 9" fill="none">
                            <path d="M15.1406 1L8.06956 8.07107L0.998489 1" stroke="#0A0A0A"/>
                            <path d="M15.1406 1L8.06956 8.07107L0.998489 1" stroke="#0A0A0A" strokeOpacity="0.2"/>
                            <path d="M15.1406 1L8.06956 8.07107L0.998489 1" stroke="#0A0A0A" strokeOpacity="0.2"/>
                            <path d="M15.1406 1L8.06956 8.07107L0.998489 1" stroke="#0A0A0A" strokeOpacity="0.2"/>
                        </svg>
                    </span>
                </button>
                <button
                    onClick={() => sortProjects('region', sortConfig.direction === 'asc' ? 'desc' : 'asc')}

                    className={s.region}
                >
                    REGION 
                    <span className={`${s.arrow} ${sortConfig.key === 'region' && sortConfig.direction === 'asc' ? s.active : ''}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="9" viewBox="0 0 16 9" fill="none">
                            <path d="M15.1406 1L8.06956 8.07107L0.998489 1" stroke="#0A0A0A"/>
                            <path d="M15.1406 1L8.06956 8.07107L0.998489 1" stroke="#0A0A0A" strokeOpacity="0.2"/>
                            <path d="M15.1406 1L8.06956 8.07107L0.998489 1" stroke="#0A0A0A" strokeOpacity="0.2"/>
                            <path d="M15.1406 1L8.06956 8.07107L0.998489 1" stroke="#0A0A0A" strokeOpacity="0.2"/>
                        </svg>
                    </span>
                </button>
                <button 
                    onClick={() => sortProjects('year', sortConfig.direction === 'asc' ? 'desc' : 'asc')}
                    className={s.year}>
                    YEAR 
                    <span className={`${s.arrow} ${sortConfig.key === 'year' && sortConfig.direction === 'asc' ? s.active : ''}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="9" viewBox="0 0 16 9" fill="none">
                            <path d="M15.1406 1L8.06956 8.07107L0.998489 1" stroke="#0A0A0A"/>
                            <path d="M15.1406 1L8.06956 8.07107L0.998489 1" stroke="#0A0A0A" strokeOpacity="0.2"/>
                            <path d="M15.1406 1L8.06956 8.07107L0.998489 1" stroke="#0A0A0A" strokeOpacity="0.2"/>
                            <path d="M15.1406 1L8.06956 8.07107L0.998489 1" stroke="#0A0A0A" strokeOpacity="0.2"/>
                        </svg>
                    </span>
                </button>
                <button 
                    onClick={() => sortProjects('status', sortConfig.direction === 'asc' ? 'desc' : 'asc')}
                    className={s.status}
                    >
                    STATUS 
                    <span className={`${s.arrow} ${sortConfig.key === 'status' && sortConfig.direction === 'asc' ? s.active : ''}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="9" viewBox="0 0 16 9" fill="none">
                            <path d="M15.1406 1L8.06956 8.07107L0.998489 1" stroke="#0A0A0A"/>
                            <path d="M15.1406 1L8.06956 8.07107L0.998489 1" stroke="#0A0A0A" strokeOpacity="0.2"/>
                            <path d="M15.1406 1L8.06956 8.07107L0.998489 1" stroke="#0A0A0A" strokeOpacity="0.2"/>
                            <path d="M15.1406 1L8.06956 8.07107L0.998489 1" stroke="#0A0A0A" strokeOpacity="0.2"/>
                        </svg>
                    </span>
                </button>
            </div>
            {sortedProjects.map((project, i) => (
                <Link href={'/projects/' + project.slug} key={i} className={s.projectLink}>
                    <motion.div 
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: 'easeOut', delay: Math.min(i * 0.05, 0.5) }}
                    viewport={{ once: true }}
                    className={s.project} key={i}
                    onMouseEnter={() => setHoveredProject(project)}
                    onMouseMove={(e) => setCursorPosition({ x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => {setHoveredProject(null); setShowImage(false);}}>
                        <div>
                            <h3>{project.title}</h3>
                        </div>
                        <div className={s.region}>
                            <h3>{project.region && project.region}</h3>
                        </div>
                        <div className={s.year}>
                            <h3>{project.year}</h3>
                        </div>
                        <div className={s.status}>
                            <h3>{project.status && project.status}</h3>
                        </div>
                    </motion.div>
                    
                </Link>
            ))}
            {hoveredProject && hoveredProject.featuredImage && hoveredProject.featuredImage.image && (
                <div 
                    ref={imageRef}
                    className={s.image} 
                    style={{ position: 'fixed', left: cursorPosition.x + imageSize.width + 20 > window.innerWidth 
                        ? cursorPosition.x - imageSize.width - 20  // Mueve la imagen a la izquierda si se pasa del límite
                        : cursorPosition.x + 20, top: cursorPosition.y - imageSize.height / 2  }} 
                >
                    <LazyImage
                        src={hoveredProject.featuredImage.image.imageUrl}
                        width={hoveredProject.featuredImage.image.metadata.dimensions.width}
                        height={hoveredProject.featuredImage.image.metadata.dimensions.height}
                        blurDataURL={hoveredProject.featuredImage.image.ref}
                        fill={false}
                        alt={hoveredProject.featuredImage.image.attribution || ""}
                        caption={hoveredProject.featuredImage.image.caption || null}
                        filename={hoveredProject.featuredImage.image.filename || ""}
                        
                    />
                </div>
            )}
        </div>
    );
    
}